package no.vareflow.app.domain

/**
 * A small QR code encoder (byte mode, error correction level M, versions 1–10), enough for a
 * login link. Follows ISO/IEC 18004 the same way as Project Nayuki's reference encoder (MIT).
 * No library is needed on the web or on Android: both draw [isDark] as squares.
 */
class QrCode private constructor(val version: Int, val mask: Int) {
    val size: Int = version * 4 + 17
    private val modules = Array(size) { BooleanArray(size) }
    private val isFunction = Array(size) { BooleanArray(size) }

    /** True for a dark module at column [x], row [y]. */
    fun isDark(x: Int, y: Int): Boolean = x in 0 until size && y in 0 until size && modules[y][x]

    /**
     * The code as an SVG image with a light border of [border] modules (4 is the standard quiet
     * zone). Dark modules are one path, so it stays small and sharp at any size.
     */
    fun toSvg(border: Int = 4, dark: String = "#000000", light: String = "#FFFFFF"): String {
        val full = size + border * 2
        val path = StringBuilder()
        for (y in 0 until size) for (x in 0 until size) {
            if (modules[y][x]) path.append("M${x + border},${y + border}h1v1h-1z")
        }
        return "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $full $full\" shape-rendering=\"crispEdges\">" +
            "<rect width=\"100%\" height=\"100%\" fill=\"$light\"/><path d=\"$path\" fill=\"$dark\"/></svg>"
    }

    companion object {
        const val MAX_VERSION = 10

        // Level M, index = version (0 unused).
        private val ECC_PER_BLOCK = intArrayOf(-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26)
        private val NUM_BLOCKS = intArrayOf(-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5)
        private const val FORMAT_BITS_M = 0

        /** Encodes [text] (UTF-8) in the smallest version that fits; throws if it is too long. */
        fun encode(text: String): QrCode {
            val data = text.encodeToByteArray()
            var version = 1
            while (true) {
                val countBits = if (version <= 9) 8 else 16
                val needed = 4 + countBits + data.size * 8
                if (needed <= dataCodewords(version) * 8) break
                version++
                require(version <= MAX_VERSION) { "Text too long for a QR code: ${data.size} bytes" }
            }
            val bits = BitBuffer()
            bits.append(0b0100, 4)
            bits.append(data.size, if (version <= 9) 8 else 16)
            data.forEach { bits.append(it.toInt() and 0xFF, 8) }
            val capacity = dataCodewords(version) * 8
            bits.append(0, minOf(4, capacity - bits.size))
            bits.append(0, (8 - bits.size % 8) % 8)
            var pad = 0xEC
            while (bits.size < capacity) { bits.append(pad, 8); pad = pad xor 0xEC xor 0x11 }
            val codewords = IntArray(bits.size / 8) { i ->
                var b = 0
                for (j in 0 until 8) b = (b shl 1) or bits[i * 8 + j]
                b
            }
            return build(version, codewords)
        }

        private fun build(version: Int, dataCodewords: IntArray): QrCode {
            // Choose the mask with the lowest penalty, like the standard says.
            var best: QrCode? = null
            var bestPenalty = Int.MAX_VALUE
            val all = addEccAndInterleave(version, dataCodewords)
            for (mask in 0 until 8) {
                val qr = QrCode(version, mask)
                qr.drawFunctionPatterns()
                qr.drawCodewords(all)
                qr.applyMask(mask)
                qr.drawFormatBits(mask)
                val penalty = qr.penalty()
                if (penalty < bestPenalty) { bestPenalty = penalty; best = qr }
            }
            return best!!
        }

        private fun rawDataModules(version: Int): Int {
            var result = (16 * version + 128) * version + 64
            if (version >= 2) {
                val numAlign = version / 7 + 2
                result -= (25 * numAlign - 10) * numAlign - 55
                if (version >= 7) result -= 36
            }
            return result
        }

        private fun dataCodewords(version: Int): Int =
            rawDataModules(version) / 8 - ECC_PER_BLOCK[version] * NUM_BLOCKS[version]

        private fun addEccAndInterleave(version: Int, data: IntArray): IntArray {
            val numBlocks = NUM_BLOCKS[version]
            val blockEccLen = ECC_PER_BLOCK[version]
            val rawCodewords = rawDataModules(version) / 8
            val numShortBlocks = numBlocks - rawCodewords % numBlocks
            val shortBlockLen = rawCodewords / numBlocks
            val divisor = reedSolomonDivisor(blockEccLen)
            val blocks = mutableListOf<IntArray>()
            var k = 0
            for (i in 0 until numBlocks) {
                val len = shortBlockLen - blockEccLen + (if (i < numShortBlocks) 0 else 1)
                val dat = data.copyOfRange(k, k + len)
                k += len
                val ecc = reedSolomonRemainder(dat, divisor)
                val block = IntArray(shortBlockLen + 1)
                dat.copyInto(block, 0)
                // Short blocks get a placeholder byte so all blocks line up for interleaving.
                val eccStart = shortBlockLen + 1 - blockEccLen
                ecc.copyInto(block, eccStart)
                blocks += block
            }
            val result = ArrayList<Int>(rawCodewords)
            for (i in 0 until shortBlockLen + 1) {
                for (j in 0 until numBlocks) {
                    if (i != shortBlockLen - blockEccLen || j >= numShortBlocks) result += blocks[j][i]
                }
            }
            return result.toIntArray()
        }

        private fun reedSolomonDivisor(degree: Int): IntArray {
            val result = IntArray(degree)
            result[degree - 1] = 1
            var root = 1
            for (i in 0 until degree) {
                for (j in result.indices) {
                    result[j] = gfMultiply(result[j], root)
                    if (j + 1 < result.size) result[j] = result[j] xor result[j + 1]
                }
                root = gfMultiply(root, 0x02)
            }
            return result
        }

        private fun reedSolomonRemainder(data: IntArray, divisor: IntArray): IntArray {
            val result = IntArray(divisor.size)
            for (b in data) {
                val factor = b xor result[0]
                for (i in 0 until result.size - 1) result[i] = result[i + 1]
                result[result.size - 1] = 0
                for (i in result.indices) result[i] = result[i] xor gfMultiply(divisor[i], factor)
            }
            return result
        }

        private fun gfMultiply(x: Int, y: Int): Int {
            var z = 0
            for (i in 7 downTo 0) {
                z = (z shl 1) xor ((z ushr 7) * 0x11D)
                z = z xor (((y ushr i) and 1) * x)
            }
            return z
        }
    }

    private class BitBuffer {
        private val bits = ArrayList<Int>()
        val size: Int get() = bits.size
        operator fun get(i: Int): Int = bits[i]
        fun append(value: Int, length: Int) {
            for (i in length - 1 downTo 0) bits += (value ushr i) and 1
        }
    }

    private fun set(x: Int, y: Int, dark: Boolean) {
        modules[y][x] = dark
        isFunction[y][x] = true
    }

    private fun drawFunctionPatterns() {
        for (i in 0 until size) {
            set(6, i, i % 2 == 0)
            set(i, 6, i % 2 == 0)
        }
        drawFinder(3, 3)
        drawFinder(size - 4, 3)
        drawFinder(3, size - 4)
        val positions = alignmentPositions()
        val n = positions.size
        for (i in 0 until n) for (j in 0 until n) {
            if ((i == 0 && j == 0) || (i == 0 && j == n - 1) || (i == n - 1 && j == 0)) continue
            drawAlignment(positions[i], positions[j])
        }
        drawFormatBits(0)
        drawVersion()
    }

    private fun drawFinder(x: Int, y: Int) {
        for (dy in -4..4) for (dx in -4..4) {
            val dist = maxOf(kotlin.math.abs(dx), kotlin.math.abs(dy))
            val xx = x + dx
            val yy = y + dy
            if (xx in 0 until size && yy in 0 until size) set(xx, yy, dist != 2 && dist != 4)
        }
    }

    private fun drawAlignment(x: Int, y: Int) {
        for (dy in -2..2) for (dx in -2..2) set(x + dx, y + dy, maxOf(kotlin.math.abs(dx), kotlin.math.abs(dy)) != 1)
    }

    private fun alignmentPositions(): IntArray {
        if (version == 1) return IntArray(0)
        val numAlign = version / 7 + 2
        val step = (version * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4) * 2
        val result = IntArray(numAlign)
        result[0] = 6
        var pos = size - 7
        for (i in numAlign - 1 downTo 1) { result[i] = pos; pos -= step }
        return result
    }

    private fun drawFormatBits(mask: Int) {
        val data = (FORMAT_BITS_M shl 3) or mask
        var rem = data
        for (i in 0 until 10) rem = (rem shl 1) xor ((rem ushr 9) * 0x537)
        val bits = ((data shl 10) or rem) xor 0x5412
        fun bit(i: Int) = ((bits ushr i) and 1) != 0
        for (i in 0..5) set(8, i, bit(i))
        set(8, 7, bit(6))
        set(8, 8, bit(7))
        set(7, 8, bit(8))
        for (i in 9 until 15) set(14 - i, 8, bit(i))
        for (i in 0 until 8) set(size - 1 - i, 8, bit(i))
        for (i in 8 until 15) set(8, size - 15 + i, bit(i))
        set(8, size - 8, true)
    }

    private fun drawVersion() {
        if (version < 7) return
        var rem = version
        for (i in 0 until 12) rem = (rem shl 1) xor ((rem ushr 11) * 0x1F25)
        val bits = (version shl 12) or rem
        for (i in 0 until 18) {
            val bit = ((bits ushr i) and 1) != 0
            val a = size - 11 + i % 3
            val b = i / 3
            set(a, b, bit)
            set(b, a, bit)
        }
    }

    private fun drawCodewords(data: IntArray) {
        var i = 0
        var right = size - 1
        while (right >= 1) {
            if (right == 6) right = 5
            for (vert in 0 until size) {
                for (j in 0 until 2) {
                    val x = right - j
                    val upward = ((right + 1) and 2) == 0
                    val y = if (upward) size - 1 - vert else vert
                    if (!isFunction[y][x] && i < data.size * 8) {
                        modules[y][x] = ((data[i ushr 3] ushr (7 - (i and 7))) and 1) != 0
                        i++
                    }
                }
            }
            right -= 2
        }
    }

    private fun applyMask(mask: Int) {
        for (y in 0 until size) for (x in 0 until size) {
            val invert = when (mask) {
                0 -> (x + y) % 2 == 0
                1 -> y % 2 == 0
                2 -> x % 3 == 0
                3 -> (x + y) % 3 == 0
                4 -> (x / 3 + y / 2) % 2 == 0
                5 -> x * y % 2 + x * y % 3 == 0
                6 -> (x * y % 2 + x * y % 3) % 2 == 0
                else -> ((x + y) % 2 + x * y % 3) % 2 == 0
            }
            if (!isFunction[y][x] && invert) modules[y][x] = !modules[y][x]
        }
    }

    private fun penalty(): Int {
        var result = 0
        for (pass in 0 until 2) {
            for (a in 0 until size) {
                var runColor = false
                var run = 0
                val history = IntArray(7)
                for (b in 0 until size) {
                    val color = if (pass == 0) modules[a][b] else modules[b][a]
                    if (color == runColor) {
                        run++
                        if (run == 5) result += 3 else if (run > 5) result++
                    } else {
                        addHistory(run, history)
                        if (!runColor) result += countFinderLike(history) * 40
                        runColor = color
                        run = 1
                    }
                }
                if (runColor) { addHistory(run, history); run = 0 }
                run += size
                addHistory(run, history)
                result += countFinderLike(history) * 40
            }
        }
        for (y in 0 until size - 1) for (x in 0 until size - 1) {
            val c = modules[y][x]
            if (c == modules[y][x + 1] && c == modules[y + 1][x] && c == modules[y + 1][x + 1]) result += 3
        }
        var dark = 0
        for (row in modules) for (m in row) if (m) dark++
        val total = size * size
        val k = (kotlin.math.abs(dark * 20 - total * 10) + total - 1) / total - 1
        result += k * 10
        return result
    }

    private fun addHistory(runLength: Int, history: IntArray) {
        val length = if (history[0] == 0) runLength + size else runLength
        for (i in history.size - 1 downTo 1) history[i] = history[i - 1]
        history[0] = length
    }

    private fun countFinderLike(h: IntArray): Int {
        val n = h[1]
        val core = n > 0 && h[2] == n && h[3] == n * 3 && h[4] == n && h[5] == n
        return (if (core && h[0] >= n * 4 && h[6] >= n) 1 else 0) + (if (core && h[6] >= n * 4 && h[0] >= n) 1 else 0)
    }
}
